import { useSyncExternalStore } from "react";
import { platform } from "@/platform";

export type WidthClass = "compact" | "medium" | "expanded" | "large";

/** client.md 2.2: compact <600dp · medium 600~839 · expanded ≥840 · large ≥1200(웹만, PC 시안 596:4497). */
export const BREAKPOINTS = { medium: 600, expanded: 840, large: 1200 } as const;

/**
 * 너비 구간별 CSS 변수(client.md 2.2 값). AppShell이 <html>에 인라인으로 심고 화면 CSS는 변수만 참조한다.
 * --content-max: 블록 컨테이너·그 외 600 / --content-max-chat: 채팅 640 / --content-max-sheet: 상점·시트 560
 * --home-cols: 홈 그리드 2/2/5/6열, --plot-card-ratio: 홈 카드 비율 173:290/173:290/5:7/5:7(시안 399:2044).
 * 모든 구간에 같은 키를 둔다 — AppShell 이 setProperty 로 덮어쓰기만 해서 빠진 키는 이전 구간 값이 남는다.
 * 플롯 프로필은 large 미만 모든 폭에서 1단(시안 Fold8·Ultra 펼침과 동일).
 * large(PC 웹): 상단 GNB·가운데 1144, 채팅 대화 칸 760, 상점 600, 바텀시트는 가운데 모달 480(BottomSheet CSS).
 */
export const LAYOUT_VARS: Record<WidthClass, Record<string, string>> = {
  compact: { "--content-max": "600px", "--content-max-chat": "640px", "--content-max-sheet": "560px", "--home-cols": "2", "--plot-card-ratio": "173 / 290" },
  medium: { "--content-max": "600px", "--content-max-chat": "640px", "--content-max-sheet": "560px", "--home-cols": "2", "--plot-card-ratio": "173 / 290" },
  expanded: { "--content-max": "600px", "--content-max-chat": "640px", "--content-max-sheet": "560px", "--home-cols": "5", "--plot-card-ratio": "5 / 7" },
  large: { "--content-max": "600px", "--content-max-chat": "760px", "--content-max-sheet": "600px", "--home-cols": "6", "--plot-card-ratio": "5 / 7" },
};

/** large 는 웹에서만. 앱(큰 태블릿 가로)은 expanded 에 머문다 — GNB·가운데 모달은 PC 브라우저 전용 시안. */
const largeEnabled = () => platform.clientKind === "web";

const queries = {
  medium: `(min-width: ${BREAKPOINTS.medium}px)`,
  expanded: `(min-width: ${BREAKPOINTS.expanded}px)`,
  large: `(min-width: ${BREAKPOINTS.large}px)`,
} as const;

function getSnapshot(): WidthClass {
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") return "compact";
  if (largeEnabled() && window.matchMedia(queries.large).matches) return "large";
  if (window.matchMedia(queries.expanded).matches) return "expanded";
  if (window.matchMedia(queries.medium).matches) return "medium";
  return "compact";
}

function subscribe(onChange: () => void): () => void {
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") return () => undefined;
  const mqls = [window.matchMedia(queries.medium), window.matchMedia(queries.expanded), window.matchMedia(queries.large)];
  for (const m of mqls) m.addEventListener("change", onChange);
  return () => {
    for (const m of mqls) m.removeEventListener("change", onChange);
  };
}

/** 현재 너비 구간. 구간이 바뀌어도 리마운트하지 않는다(key 금지). */
export function useWidthClass(): WidthClass {
  return useSyncExternalStore(subscribe, getSnapshot, () => "compact");
}

/** 홈 그리드 열 수. PlotGrid가 data-cols로 노출한다. */
export const homeColumns = (wc: WidthClass): number => Number(LAYOUT_VARS[wc]["--home-cols"]);
